/**
 * DECIDED 2026-08-15: when Qualitas coverage goes live the GUEST pays the
 * premium. `platform.dailyCents` is the FALLBACK guest-facing daily premium
 * used only while a vehicle awaits its own real quote
 * (`vehicles.coverage_daily_rate_cents`, filled by quote-coverage-premiums
 * against the insurer's production cotizacion endpoint). The quoted per-vehicle
 * rate wins in pricing-core; this constant is the fail-open floor so a booking
 * never breaks. Value validated 2026-09-06: a 1-day production quote for a
 * $28,700 sum insured = $10.76 total (prima neta + $25/365 prorated derecho +
 * recargo + IVA included). Pass-through to the insurer, NOT Tago revenue;
 * `included: true` means "no selectable plans", NOT "free". MUST mirror
 * supabase/functions/_shared/constants.ts. Go-live flip order: see
 * supabase/functions/_shared/coverage.ts header and
 * docs/runbooks/coverage-golive.md.
 */
export const INSURANCE_RATES = {
  platform: { dailyCents: 1076, deductibleCents: 0, included: true },
} as const

/** Single included coverage model; never expose selectable plans. */
/** Coverage detail i18n keys for the single included platform coverage model. */
export const INSURANCE_COVERAGE_KEYS = {
  platform: [
    'insurance.included_point_auto',
    'insurance.included_point_no_plans',
    'insurance.included_point_assistance',
    'insurance.included_point_benefits',
  ],
} as const

/** Public coverage surface. Keep insurer identity hidden until a final launch is approved. */
export const INSURANCE_PROVIDER = {
  legalName: 'Cobertura de plataforma',
  displayName: 'Cobertura',
  brandColor: '#F0422D',
  coverageUrl: null,
  contactEmail: null,
  contactPhone: null,
  regulator: 'Superintendencia del Sistema Financiero (SSF)',
  foundedYear: null,
  address: 'San Salvador',
} as const

export const EARLY_ACCESS = {
  individualHostingEnabled: true,
  platformInsuranceEnabled: true,
  hostInsuranceModel: 'fleet',
  /**
   * When true, hosts must wait for Qualitas/OFAC approval (`approved`)
   * before listing. When false (launch default until partner list API
   * is live), submitting the AML form (`under_review`) is enough to
   * list — data is still exported for offline Qualitas validation.
   */
  amlHostRequiresPartnerApproval: false,
} as const

/**
 * Platform economics.
 *
 * Hosts receive their advertised rental subtotal intact. Tago's marketplace
 * take is charged to the guest as 15% of that host subtotal, then any real
 * add-ons/taxes are applied through the pricing utility.
 *
 * Keep hostFeePct at 0: this is the invariant that prevents the host payout
 * from being reduced by Tago. Processor/bank costs are reconciliation costs;
 * they are not deducted from host payout in checkout pricing.
 *
 * MUST mirror apps/mobile/supabase/functions/_shared/constants.ts —
 * both implementations feed into payment creation, webhook validation,
 * and refund math. A 1-cent divergence rejects the Wompi capture.
 */
export const PLATFORM_FEES = {
  guestServiceFeePct: 15,
  hostFeePct: 0,
  superhostFeePct: 15,
  // Payment-processing fee % the HOST absorbs on an optional security deposit
  // (Wompi's effective rate, IVA-incl). The guest is refunded the deposit in
  // full; this % of the deposit is deducted from the host's earnings.
  depositProcessingFeePct: 3.5,
  // Payment-processing fee % grossed up ON TOP of Tago's 15% so Tago nets 100%
  // of its take (Wompi's effective rate, IVA-incl). Guest-paid pass-through,
  // not Tago revenue: the DTE invoices the 15% only.
  guestServiceFeeProcessingPct: 3.5,
} as const

/**
 * Same guest+host pair: 15% on the first completed trip, 8% on the second,
 * 5% from the third. Default 15% is the first-match rate. MUST mirror
 * supabase/functions/_shared/constants.ts. Independence promo does not
 * read this table — it still zeros whatever fee the pair currently pays.
 */
export const TIERED_FEE_POLICY = {
  version: 1,
  defaultPct: 15,
  tiers: [
    { minCompletedPairTrips: 0, pct: 15 },
    { minCompletedPairTrips: 1, pct: 8 },
    { minCompletedPairTrips: 2, pct: 5 },
  ],
} as const

/** El Salvador VAT (IVA). Temporarily not applied during the launch pilot. */
export const TAX_RATES = {
  ivaPct: 0,
} as const

export const LATE_RETURN = {
  gracePeriodMinutes: 30,
  tier1PctOfDaily: 25,
  tier2PctOfDaily: 50,
  tier3Days: 1,
  /**
   * Absolute cap on late-return fee days. Beyond this, the vehicle is
   * treated as abandoned / disputed and daily charges stop accruing. Mirrors
   * the server-side cap in supabase/functions/_shared/constants.ts. Must
   * match that value exactly.
   */
  maxLateDays: 30,
} as const

export const BOOKING_LIMITS = {
  autoDeclineHours: 24,
  paymentDeadlineHours: 24,
  // Published promise (2026-08-15): ALL hosts are paid within 48 hours — the
  // owner committed to this operationally (payouts are disbursed manually until
  // an automated payout system exists). New hosts are NOT held longer, so this
  // matches payoutHoldHours; a future payout scheduler must honor 48h for both.
  payoutHoldHours: 48,
  newHostPayoutHoldHours: 48,
  damageReportWindowHours: 24,
  reviewWindowDays: 14,
} as const

export const AGE_REQUIREMENTS = {
  minimumBooking: 18,
  premiumVehicle: 25,
} as const

export const RENTAL_LIMITS = {
  minHours: 4,
  maxDays: 30,
} as const

export const DEFAULT_KM = {
  dailyLimit: null as number | null,
  excessRateCents: null as number | null,
} as const

export type InsuranceTier = keyof typeof INSURANCE_RATES
